import React from 'react';
import { Link } from 'react-router-dom';
import { Dna } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { BrandWordmark } from './brand/BrandWordmark';
import { StaffFooterLink } from './layout/StaffFooterLink';

/**
 * Shared footer for landing page, FAQ, and app.
 * variant: "dark" for landing/FAQ (dark bg), "light" for app (heritage theme)
 */
const AppFooter = ({ variant = 'dark' }) => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  const isDark = variant === 'dark';

  const linkClass = isDark
    ? 'text-sm text-white/40 hover:text-white/60 transition-colors'
    : 'text-sm text-muted-foreground hover:text-foreground transition-colors';

  const logoClass = isDark
    ? 'flex items-center gap-2 text-white/40 hover:text-white/60 transition-colors'
    : 'flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors';

  const copyrightClass = isDark ? 'text-sm text-white/30' : 'text-sm text-muted-foreground/70';

  const borderClass = isDark ? 'border-t border-white/5' : 'border-t border-border/40';

  return (
    <footer
      id="contact"
      className={`relative scroll-mt-24 py-8 ${borderClass} mt-auto`}
      data-testid="app-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <Link to="/home" className={logoClass}>
            <Dna className="w-5 h-5" />
            <BrandWordmark
              variant={isDark ? 'footerOnDark' : 'footerOnLight'}
              className="text-sm"
            />
          </Link>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left">
            <p className={`text-sm max-w-xs ${isDark ? 'text-white/50' : 'text-muted-foreground'}`}>
              Preserving family stories for future generations.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/pricing" className={linkClass}>
                Pricing
              </Link>
              <Link to="/faq" className={linkClass}>
                FAQ
              </Link>
              <Link to="/help" className={linkClass}>
                Help
              </Link>
              {user && (
                <Link to="/dashboard" className={linkClass}>
                  Dashboard
                </Link>
              )}
              <StaffFooterLink variant={isDark ? 'dark' : 'light'} />
              <span className={`inline-flex flex-wrap items-baseline justify-center gap-x-1 ${copyrightClass}`}>
                © 2026
                <BrandWordmark
                  variant={isDark ? 'footerCopyrightDark' : 'footerCopyrightLight'}
                  as="span"
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AppFooter;
